import React from 'react'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import * as PropTypes from 'prop-types'
import { GRAY_COLORS } from 'asterX-UI/colors.constant'
import { Box, Button, Chip, Grid, Popper, Typography, Divider, Grow } from '@material-ui/core'
import { ErrorOutline } from '@material-ui/icons'
import clsx from 'clsx'

const StyledTableCell = withStyles(theme => ({
  root: { position: 'static' },
  head: {
    backgroundColor: 'transparent',
    color: GRAY_COLORS[600],
    padding: '8px',
    fontSize: 'var(--th-font-size , 14px)',
    '&:first-child': { padding: '8px' },
    whiteSpace: 'var(--th-white-space, nowrap)',
    wordBreak: 'var(--th-word-break, normal)',
    overflowWrap: 'var(--th-overflow-wrap, normal)'
  },
  body: {
    color: GRAY_COLORS[600],
    padding: '8px',
    '&:first-child': { padding: '8px' },
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'var(--td-white-space, normal)',
    wordBreak: 'var(--td-word-break, normal)',
    overflowWrap: 'var(--td-overflow-wrap, normal)',
    maxWidth: 'var(--td-max-width, none)'
  }
}))(TableCell)

const StyledTableRow = withStyles(theme => ({
  root: {
    position: 'relative',
    '&:nth-child(odd)': { backgroundColor: GRAY_COLORS[50] },
    cursor: props => (props?.clickable ? 'pointer' : 'default'),
    '&:hover': {
      backgroundColor: props => (props?.clickable ? GRAY_COLORS[50] : '')
    }
  }
}))(TableRow)

const useStyles = makeStyles(theme => ({
  tableContainer: {
    boxShadow: 'unset',
    border: `1px solid ${GRAY_COLORS[200]}`,
    borderRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    marginBottom: 20
  },
  bgClick: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: 0,
    top: 0,
    opacity: 0
  },
  table: {
    minWidth: 700,
    [theme.breakpoints.down('md')]: { minWidth: 935 }
  },
  tableHead: { backgroundColor: 'transparent' },

  tooltipRoot: {
    zIndex: 1600
  },
  copyTooltipPopper: {
    zIndex: 2200
  },
  tooltipCard: {
    background: '#fff',
    border: `1px solid ${GRAY_COLORS[200]}`,
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    borderRadius: 12,
    padding: 12,
    maxWidth: "140px",
    userSelect: 'text',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column'
  },
  tooltipHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6
  },
  tooltipHeaderRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center'
  },
  tooltipIcon: {
    fontSize: 18,
    color: GRAY_COLORS[700]
  },
  tooltipTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: GRAY_COLORS[800]
  },
  tooltipDivider: {
    margin: '6px 0 8px'
  },
  tooltipText: {
    fontSize: 13,
    color: GRAY_COLORS[700],
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  tooltipArrow: {
    position: 'absolute',
    width: 10,
    height: 10,
    background: '#fff',
    borderLeft: `1px solid ${GRAY_COLORS[200]}`,
    borderTop: `1px solid ${GRAY_COLORS[200]}`,
    transform: 'rotate(45deg)',
    right: 12,
    top: -5
  },

  variantError: {
    background: '#fff5f5',
    borderColor: '#f44336'
  },
  variantWarning: {
    background: '#fff8e1',
    borderColor: '#ff9800'
  },
  variantInfo: {
    background: '#e3f2fd',
    borderColor: '#2196f3'
  },
  variantSuccess: {
    background: '#f1f8e9',
    borderColor: '#4caf50'
  }
}))


function normalizeTooltipContent(content) {
  if (content && typeof content === 'object' && !React.isValidElement(content)) {
    return {
      type: content.type || 'info',
      title: content.title || (content.type === 'error' ? 'Ошибка' : 'Информация'),
      message: content.message || '',
      icon: content.icon || (content.type === 'error' ? <ErrorOutline style={{ color: '#f44336' }} /> : null),
      node: content.node || null
    }
  }

  if (React.isValidElement(content)) {
    return { type: 'info', title: null, message: null, icon: null, node: content }
  }
  if (typeof content === 'string') {
    return { type: 'info', title: 'Информация', message: content, icon: null, node: null }
  }
  return null
}

export default function StandardTable({
                                        tableData,
                                        tableStyles,
                                        onRowClick,
                                        hasBackgroudRowClick = false,
                                        onButtonClick,
                                        onChipClick,
                                        btnColor = 'primary',
                                        btnVariant = 'outlined',
                                        btnStyle = {},
                                        className,
                                        columnsToTotal = [],
                                        rowTooltip,
                                        thAdaptive = false,
                                        thFontSize,
                                        columnMaxWidths = {}
                                      }) {
  const classes = useStyles()

  const colStyles = React.useMemo(() => {
    const out = {}
    if (columnMaxWidths && typeof columnMaxWidths === 'object') {
      Object.keys(columnMaxWidths).forEach(k => {
        const width = columnMaxWidths[k]
        const size = typeof width === 'number' ? `${width}px` : width
        out[Number(k)] = {
          maxWidth: size,
          width: size,
          overflow: 'hidden',
          whiteSpace: 'normal',
          textWrap:'wrap'
        }
      })
    }
    return out
  }, [columnMaxWidths])

  const computeTotals = () => {
    const totals = Array(tableData?.columns?.length).fill(0)
    tableData?.rows.forEach(row => {
      row.forEach((cell, index) => {
        if (columnsToTotal?.includes(index)) {
          const cellValue = parseFloat(cell?.toString()?.replace(/[^\d.-]/g, ''))
          if (!isNaN(cellValue)) totals[index] += cellValue
        }
      })
    })
    return totals.map(total => (total !== 0 ? `${total?.toString()?.thousandSeparator()} ₸` : ''))
  }

  const totals = computeTotals()

  const [anchorEl, setAnchorEl] = React.useState(null)
  const [tooltipContent, setTooltipContent] = React.useState(null)
  const [isOverPopper, setIsOverPopper] = React.useState(false)

  const openTimerRef = React.useRef(null)
  const closeTimerRef = React.useRef(null)
  const contentRef = React.useRef(null)

  const handleOpenFromRow = (rowEl, row) => {
    if (!rowEl || !rowTooltip) return
    const raw = typeof rowTooltip === 'function' ? rowTooltip(row) : rowTooltip
    const normalized = normalizeTooltipContent(raw)
    if (!normalized) {
      setAnchorEl(null)
      setTooltipContent(null)
      return
    }

    const btnIdx = Array.isArray(row) ? row.findIndex(c => c && c.isButton) : -1
    const tdByIdx = btnIdx >= 0 ? rowEl.querySelector(`td:nth-child(${btnIdx})`) : null
    const targetTd = tdByIdx || rowEl.querySelector('td:last-child') || rowEl

    setTooltipContent(normalized)
    setAnchorEl(targetTd)
  }

  const scheduleOpen = (e, row) => {
    clearTimeout(closeTimerRef.current)
    const rowEl = e.currentTarget
    openTimerRef.current = setTimeout(() => handleOpenFromRow(rowEl, row), 120)
  }

  const scheduleClose = (force = false, timeOut = 120) => {
    clearTimeout(openTimerRef.current)
    clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      if (force || !isOverPopper) {
        setAnchorEl(null)
        setTooltipContent(null)
      }
    }, timeOut)
  }


  const variantClass =
    tooltipContent?.type === 'error'
      ? classes.variantError
      : tooltipContent?.type === 'warning'
      ? classes.variantWarning
      : tooltipContent?.type === 'success'
      ? classes.variantSuccess
      : classes.variantInfo

  return (
    <TableContainer className={clsx(classes.tableContainer, className)} component={Paper} style={tableStyles}>
      <Table stickyHeader className={classes.table} aria-label="customized table">
        <TableHead
          className={classes.tableHead}
          style={
            thAdaptive
              ? { '--th-font-size': 'clamp(8px, 1.2vw, 14px)' }
              : thFontSize
                ? { '--th-font-size': typeof thFontSize === 'number' ? `${thFontSize}px` : thFontSize }
                : undefined
          }
        >
          <TableRow>
            {tableData?.columns?.map((name, index) => (
              <StyledTableCell key={`styled_name_${index}`} style={colStyles[index]}>{name}</StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData?.rows?.map((row, index) => (
            <StyledTableRow
              key={`styled_row_${index}`}
              clickable={!!onRowClick}
              onClick={() => !hasBackgroudRowClick && onRowClick?.(row)}
              onMouseEnter={e => scheduleOpen(e, row)}
              onMouseLeave={scheduleClose}
            >
              {row?.map((val, indx) => {
                return (
                  <StyledTableCell key={`styled_cell_${indx}`} style={colStyles[indx]}>
                    {hasBackgroudRowClick && <Box className={classes.bgClick} onClick={() => onRowClick?.(row)}></Box>}

                    {val?.label ? (
                      <Grid container spacing={1} justifyContent={val?.isButton ? 'flex-end' : 'flex-start'}>
                        <Grid item>
                          {val?.isButton && (
                            <Button
                              size="small"
                              color={btnColor}
                              variant={btnVariant}
                              style={{ ...btnStyle, ...(val?.style || {}) }}
                              onClick={() => onButtonClick?.(val?.data ? val?.data : row)}
                            >
                              {val?.label}
                            </Button>
                          )}

                          {val?.isChip && (
                            <Chip
                              label={val?.label}
                              style={{ ...btnStyle, ...(val?.style || {}) }}
                              onClick={() => onChipClick?.(val?.data, row)}
                            />
                          )}
                        </Grid>
                      </Grid>
                    ) : (
                      val
                    )}
                  </StyledTableCell>
                )
              })}
            </StyledTableRow>
          ))}

          <Popper
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            placement="bottom-end"
            transition
            className={classes.tooltipRoot}
            onMouseEnter={() => {
              clearTimeout(closeTimerRef.current)
              setIsOverPopper(true)
            }}
            onMouseLeave={() => {
              setIsOverPopper(false)
              scheduleClose()
            }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} timeout={140}>
                <Box position="relative">
                  <Box className={clsx(classes.tooltipCard, variantClass)}>
                    {tooltipContent && tooltipContent.node ? (
                      <div ref={contentRef}>{tooltipContent.node}</div>
                    ) : (
                      <>
                        <div className={classes.tooltipHeader}>
                          {tooltipContent?.icon ||
                            (tooltipContent?.type === 'error' ? (
                              <ErrorOutline className={classes.tooltipIcon} />
                            ) : null)}
                          {tooltipContent?.title && (
                            <Typography className={classes.tooltipTitle}>{tooltipContent.title}</Typography>
                          )}
                        </div>
                        {(tooltipContent?.title || tooltipContent?.icon) && (
                          <Divider className={classes.tooltipDivider} />
                        )}
                        <div ref={contentRef}>
                          <Typography className={classes.tooltipText}>{tooltipContent?.message}</Typography>
                        </div>
                      </>
                    )}
                  </Box>
                </Box>
              </Grow>
            )}
          </Popper>

          {columnsToTotal?.length > 0 && (
            <StyledTableRow>
              {tableData.columns.map((col, index) => (
                <StyledTableCell key={`styled_total_${index}`}>
                  {columnsToTotal.includes(index) ? totals[index] : ''}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

StandardTable.propTypes = {
  tableData: PropTypes.shape({
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired
  }).isRequired,
  tableStyles: PropTypes.object,
  onRowClick: PropTypes.func,
  hasBackgroudRowClick: PropTypes.bool,
  onButtonClick: PropTypes.func,
  onChipClick: PropTypes.func,
  btnColor: PropTypes.string,
  btnVariant: PropTypes.string,
  btnStyle: PropTypes.object,
  className: PropTypes.string,
  columnsToTotal: PropTypes.arrayOf(PropTypes.number),
  rowTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node, PropTypes.object]),
  thAdaptive: PropTypes.bool,
  thFontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  columnMaxWidths: PropTypes.object
}
